import React, { useState, useEffect, useContext, useRef, useMemo } from 'react';
import Select from 'react-select';
import { useSelector, useDispatch } from 'react-redux';
import pt from 'date-fns/locale/pt-BR';
import DatePicker, { registerLocale } from 'react-datepicker';
import { ThemeContext } from 'styled-components';
import * as s from './styled-reports';
import { Loader, Breadcumb } from '../../components';
import {
  fetchAllCustomersCompany,
  fetchAllParksCustomer,
  getVariableGroups,
  getAllVariables,
  getThingSetPoints,
  getNewModelOfReportV3,
} from '../../services';
import {
  newFormatDateReports,
  newFormatDateButton,
  notifyError,
  notifySuccess,
  notifyWarning,
} from '../../utils';
import {
  listPresets,
  savePreset,
  deletePreset,
  listHistory,
  recordHistory,
  clearHistory,
} from './storage';
import 'react-datepicker/dist/react-datepicker.css';

// Normaliza selecionado.thing pra array — pode ser objeto unico (single
// selection) OU array (multi). Resto do codigo lida sempre com array.
const asThingsArray = (val) => {
  if (!val) return [];
  if (Array.isArray(val)) return val;
  return [val];
};

registerLocale('pt', pt);
const SETPOINT_KEY = 'setpoint-';

// Resolvido uma vez no module-load: evita recriar essa expressao a cada
// render e a cada propagacao pro react-select (que reinstala portal).
const PORTAL_TARGET =
  typeof document !== 'undefined' ? document.body : null;

// Presets de periodo: configuram dataInicio/dataFinal de uma vez.
// Operador clica e ja tem o periodo pronto — economiza 2 cliques no
// DatePicker e e a operacao mais comum (80% dos casos cabem em 1 desses).
const PERIOD_PRESETS = [
  {
    key: '7d',
    label: 'Últimos 7d',
    compute: () => {
      const end = new Date();
      const start = new Date();
      start.setDate(end.getDate() - 7);
      return { start, end };
    },
  },
  {
    key: '30d',
    label: 'Últimos 30d',
    compute: () => {
      const end = new Date();
      const start = new Date();
      start.setDate(end.getDate() - 30);
      return { start, end };
    },
  },
  {
    key: 'currentMonth',
    label: 'Mês atual',
    compute: () => {
      const now = new Date();
      const start = new Date(now.getFullYear(), now.getMonth(), 1);
      const end = now;
      return { start, end };
    },
  },
  {
    key: 'previousMonth',
    label: 'Mês anterior',
    compute: () => {
      const now = new Date();
      const start = new Date(now.getFullYear(), now.getMonth() - 1, 1);
      const end = new Date(now.getFullYear(), now.getMonth(), 0); // ultimo dia do mes anterior
      return { start, end };
    },
  },
];

// Compara duas datas (D/M/Y) ignorando tempo — usado pra detectar qual
// preset esta ativo baseado nas datas atuais do form.
const sameYMD = (a, b) => {
  if (!a || !b) return false;
  return (
    a.getFullYear() === b.getFullYear() &&
    a.getMonth() === b.getMonth() &&
    a.getDate() === b.getDate()
  );
};

const Relatorios = () => {
  const theme = useContext(ThemeContext);
  const dispatch = useDispatch();
  const { user } = useSelector((state) => state.usuario);
  // Smart defaults: dataInicio = D-7 ja preenchida. Antes era undefined,
  // operador precisava abrir o picker do zero. Default cobre 80% dos
  // casos de uso (relatorio dos ultimos 7 dias).
  const [dataInicio, setDataInicio] = useState(() => {
    const d = new Date();
    d.setDate(d.getDate() - 7);
    return d;
  });
  const [dataFinal, setDataFinal] = useState(new Date());
  const [allVariaveis, setVariaveis] = useState([]);
  const [loading, setLoading] = useState(false);
  const [selecionado, setSelecionado] = useState({
    cliente: null,
    aplicacao: null,
    thing: null,
  });
  const [lista, setLista] = useState({
    cliente: [],
    aplicacao: [],
    thing: [],
  });
  const [optionsAllFavorites, setOptionsAllFavorites] = useState([]);
  const [testeList, setTesteList] = useState([]);
  const [allSetpoints, setAllSetpoints] = useState([]);
  // Presets salvos pelo operador (localStorage). Estado local que reflete
  // o que esta gravado. Re-fetched depois de cada save/delete.
  const [presets, setPresets] = useState(() => listPresets());
  const [presetName, setPresetName] = useState('');
  // Historico recente dos ultimos N relatorios gerados — auto-salvo em
  // localStorage apos cada geracao bem-sucedida. Operador clica
  // "Regerar" pra refazer um relatorio identico (defaultando o destino,
  // periodo e variaveis ao que era na primeira vez).
  const [history, setHistory] = useState(() => listHistory());
  // Progresso da geracao multi-thing: { current, total }. null = ocioso.
  // Mostra 'Gerando 2 de 5...' quando varias maquinas selecionadas.
  const [genProgress, setGenProgress] = useState(null);
  // Ref de cancelamento (precisa ser ref, nao state — closure do loop
  // assincrono captura o valor inicial). Quando true, o loop de geracao
  // para na proxima iteracao. NAO aborta requests em-voo (precisaria
  // AbortController integrado em getNewModelOfReportV3 + request.utils).
  const cancelRef = useRef(false);
  const [cancelRequested, setCancelRequested] = useState(false);

  // styles do react-select: cada chamada cria objeto novo, o que faz o
  // react-select reprocessar todo o css-in-js. Memoizado por theme.
  const selectStyles = useMemo(() => s.customStyles(theme), [theme]);
  const selectStylesVariables = useMemo(
    () => s.customStylesVariables(theme),
    [theme],
  );

  useEffect(() => {
    if (user.company && user.company.id) {
      fetchAllCustomersCompany(user.company.id)
        .then(({ data }) => {
          const cliente = data.map(({ id, name }) => ({
            label: name,
            value: parseInt(id, 10),
          }));
          cliente.sort((a, b) =>
            a.label < b.label ? -1 : a.label > b.label ? 1 : 0
          );
          setLista((l) => ({ ...l, cliente }));
        })
        .catch(() => notifyError('Não foi possível buscar os clientes'));
    } else if (user.customer) {
      setLista((l) => ({
        ...l,
        cliente: [{ value: user.customer.id, label: user.customer.name }],
      }));
    }
  }, [user]);

  useEffect(() => {
    // Em multi-thing, usa a PRIMEIRA maquina como referencia pra listar
    // variaveis disponiveis. Assume que maquinas da mesma aplicacao tem
    // variaveis similares — premissa razoavel pra usina/parque.
    const firstThing = asThingsArray(selecionado.thing)[0];
    if (firstThing && firstThing.value) {
      getVariableGroups(parseInt(firstThing.value, 10))
        .then(({ data }) => {
          const supervisorioId = data
            .filter(({ group }) => group === 'supervisorio')
            .pop().id;
          getAllVariables(
            parseInt(firstThing.value, 10),
            1,
            50,
            supervisorioId
          )
            .then((resp) => {
              setOptionsAllFavorites(
                resp.data.map(({ id, ihmname, name, unit }) => ({
                  id,
                  ihmname,
                  name,
                  unit,
                }))
              );
            })
            .catch(() => {
              notifyWarning('Erro ao buscar as variáveis');
            });
        })
        .catch(() =>
          notifyError('Não foi possível buscar os grupos de variáveis')
        );
    }
  }, [selecionado]);

  useEffect(() => {
    if (selecionado.aplicacao) {
      const thing = lista.aplicacao
        .filter(({ label }) => label === selecionado.aplicacao.label)[0]
        .thing.map(({ thing: t, id }) => ({
          label: t,
          value: parseInt(id, 10),
        }));
      setLista((l) => ({ ...l, thing: [...thing] }));
    } else if (selecionado.cliente) {
      fetchAllParksCustomer(selecionado.cliente.value)
        .then(({ data }) => {
          const aplicacoes = data
            .map(({ appliance }) => appliance)
            .flat(1)
            .map(({ id, description, thing }) => ({
              label: description,
              value: id,
              thing,
            }));
          setLista((l) => ({
            ...l,
            thing: [],
            aplicacao: aplicacoes,
          }));
        })
        .catch(() => notifyError('Não foi possível buscar as aplicações'));
    }
    // lista.aplicacao e lido aqui mas setLista usa forma funcional;
    // adicionar lista causaria loop
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selecionado]);

  // Aplica um preset de periodo (D-7, D-30, mes atual, mes anterior).
  const applyPeriodPreset = (preset) => {
    const { start, end } = preset.compute();
    setDataInicio(start);
    setDataFinal(end);
  };

  // Detecta qual preset (se algum) bate com as datas atuais — pra
  // destacar o chip selecionado. Memoizado pra evitar Date constructors
  // a cada render (preset.compute() instancia datas).
  const activePresetKey = useMemo(() => {
    if (!dataInicio || !dataFinal) return null;
    const match = PERIOD_PRESETS.find((preset) => {
      const { start, end } = preset.compute();
      return sameYMD(start, dataInicio) && sameYMD(end, dataFinal);
    });
    return match ? match.key : null;
  }, [dataInicio, dataFinal]);

  const handleDateChange = (newDate, type) => {
    if (type === 'dataInicio') {
      if (newDate > dataFinal)
        notifyWarning('Data de inicio não pode ser maior que a data final!');
      else setDataInicio(newDate);
    } else if (type === 'dataFinal') {
      if (newDate < dataInicio)
        notifyWarning('Data de inicio não pode ser maior que a data final!');
      else setDataFinal(newDate);
    }
  };

  const getVariablesAndSetpointsIdsSelected = () => {
    let setpointsIds = allVariaveis.filter((id) => id.includes(SETPOINT_KEY));
    if (setpointsIds.length)
      setpointsIds = setpointsIds.map((id) =>
        parseInt(id.replace(SETPOINT_KEY, ''), 10)
      );

    let normalVariablesIds = allVariaveis.filter(
      (id) => !id.includes(SETPOINT_KEY)
    );
    if (normalVariablesIds.length)
      normalVariablesIds = normalVariablesIds.map((id) => parseInt(id, 10));

    return [normalVariablesIds, setpointsIds];
  };

  // Geracao de relatorio. Suporta MULTI-thing: quando o operador
  // selecionou varias maquinas, geramos um XLS por maquina sequencialmente
  // (endpoint backend e single-thing). Progresso visivel via genProgress.
  const handleGenerateNewReport = async () => {
    const things = asThingsArray(selecionado.thing);
    if (!things.length) return;

    setLoading(true);
    cancelRef.current = false;
    setCancelRequested(false);
    const formatedInitialDate = `${
      dataInicio.toISOString().split('T')[0]
    }T00:00:00.000Z`;
    const formatedEndDate = `${
      dataFinal.toISOString().split('T')[0]
    }T23:59:59.999Z`;

    const [normalVariablesIds, setpointsIds] =
      getVariablesAndSetpointsIdsSelected();

    const variables = optionsAllFavorites.filter((v) =>
      normalVariablesIds.includes(Number(v.id))
    );

    let setpoints = allSetpoints.filter((setpoint) =>
      setpointsIds.includes(Number(setpoint.id))
    );
    if (setpoints.length)
      setpoints = setpoints.map(({ variable, label, unit }) => ({
        variable,
        label,
        unit,
      }));

    let okCount = 0;
    let failCount = 0;

    for (let i = 0; i < things.length; i += 1) {
      if (cancelRef.current) break;
      const thing = things[i];
      setGenProgress({ current: i + 1, total: things.length });
      try {
        // eslint-disable-next-line no-await-in-loop
        const resp = await getNewModelOfReportV3(
          thing.label,
          formatedInitialDate,
          formatedEndDate,
          variables,
          setpoints
        );
        const url = window.URL.createObjectURL(new Blob([resp]));
        const link = document.createElement('a');
        link.href = url;
        const suffix = things.length > 1 ? `_${thing.label}` : '';
        link.setAttribute(
          'download',
          `relatorio_higra${suffix}_${newFormatDateReports(
            dataInicio
          )}_ate_${newFormatDateReports(dataFinal)}.xls`
        );
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        window.URL.revokeObjectURL(url);

        // Historico: 1 entry por geracao (mesmo em multi-thing).
        recordHistory({
          thingLabel: thing.label,
          aplicacaoLabel: selecionado.aplicacao?.label || null,
          clienteLabel: selecionado.cliente?.label || null,
          periodLabel: `${newFormatDateButton(dataInicio)} — ${newFormatDateButton(dataFinal)}`,
          variableCount: allVariaveis.length,
          destination: {
            clienteId: selecionado.cliente?.value || null,
            clienteLabel: selecionado.cliente?.label || null,
            aplicacaoId: selecionado.aplicacao?.value || null,
            aplicacaoLabel: selecionado.aplicacao?.label || null,
            thingId: thing.value,
            thingLabel: thing.label,
          },
          periodPresetKey: activePresetKey || 'custom',
          variableIds: allVariaveis,
        });
        okCount += 1;
      } catch (e) {
        failCount += 1;
        try {
          // eslint-disable-next-line no-await-in-loop
          const jsonError = await new Response(e).json();
          if (jsonError.message)
            notifyWarning(`${thing.label}: ${jsonError.message}`);
        } catch {
          notifyWarning(`Falha ao gerar relatório de ${thing.label}`);
        }
      }
    }

    setHistory(listHistory());
    setGenProgress(null);
    setLoading(false);

    if (cancelRef.current) {
      notifyWarning(
        `Geração cancelada apos ${okCount} relatório${okCount !== 1 ? 's' : ''}.`
      );
      return;
    }

    if (things.length === 1 && okCount === 1) {
      notifySuccess('Relatorio gerado com sucesso!');
    } else if (okCount > 0) {
      const plural = okCount > 1 ? 's' : '';
      const failNote = failCount > 0 ? ` (${failCount} falharam)` : '';
      notifySuccess(
        `${okCount} relatório${plural} gerado${plural}${failNote}`
      );
    }
  };

  // Options agrupadas do Select de variaveis: memoizadas pra evitar
  // recriar arrays (e fazer o react-select reprocessar) a cada render.
  const variableSelectOptions = useMemo(() => {
    const groups = [
      {
        label: 'Variáveis',
        options: optionsAllFavorites.map((v) => ({
          label: v.name,
          value: v.id,
        })),
      },
      {
        label: 'Setpoints',
        options:
          allSetpoints.length > 0
            ? allSetpoints.map(({ label, id }) => ({
                label,
                value: `${SETPOINT_KEY}${id}`,
              }))
            : [],
      },
    ];
    return groups.filter((g) => g.options.length > 0);
  }, [optionsAllFavorites, allSetpoints]);

  useEffect(() => {
    // Setpoints: tambem usa a 1a maquina como referencia (igual variaveis).
    const firstThing = asThingsArray(selecionado.thing)[0];
    const thingId = firstThing?.value;
    if (thingId) {
      getThingSetPoints(thingId).then(({ data }) => {
        setAllSetpoints(data.map(({ setpoint }) => setpoint));
      });
    }
  }, [selecionado.thing]);

  // Breadcrumb resolve o dead-end da tela: antes, o operador entrava em
  // Relatorios pelo side-menu e nao tinha indicacao clara de como voltar.
  // Customer ve apenas "Relatorios"; higra/admin veem Dashboard > Relatorios.
  const breadcrumbPaths =
    user && user.customer
      ? [{ pathName: 'Relatórios', to: '/reports' }]
      : [
          {
            pathName: 'Dashboard',
            to: '/dashboard',
            action: () => dispatch({ type: 'SEGUNDO/RESET' }),
          },
          { pathName: 'Relatórios', to: '/reports' },
        ];

  // === Presets ===
  // Salva a configuracao atual (destino + variaveis + periodo) com o nome
  // digitado pelo operador. Se nome ja existe, faz UPSERT (substitui).
  const handleSavePreset = () => {
    const trimmed = presetName.trim();
    if (!trimmed) {
      notifyWarning('Dê um nome ao preset antes de salvar');
      return;
    }
    const things = asThingsArray(selecionado.thing);
    if (things.length === 0) {
      notifyWarning('Selecione pelo menos uma máquina antes de salvar o preset');
      return;
    }
    // Preset salva a PRIMEIRA maquina (single-thing). Caso operador
    // queira multi-thing, gera o relatorio e usa o historico depois.
    // Simplifica restauracao do preset.
    const firstThing = things[0];
    const preset = {
      name: trimmed,
      destination: {
        clienteId: selecionado.cliente?.value || null,
        clienteLabel: selecionado.cliente?.label || null,
        aplicacaoId: selecionado.aplicacao?.value || null,
        aplicacaoLabel: selecionado.aplicacao?.label || null,
        thingId: firstThing.value,
        thingLabel: firstThing.label,
      },
      periodPresetKey: activePresetKey || 'custom',
      variableIds: allVariaveis,
    };
    savePreset(preset);
    setPresets(listPresets());
    setPresetName('');
    notifySuccess(`Preset "${trimmed}" salvo`);
  };

  // Carrega um preset. Reseta os selects (clieente/appliance/thing) e
  // aplica o periodo. Variaveis precisam aguardar o thing carregar a
  // lista — entao salvamos os IDs num ref e o useEffect aplica quando
  // optionsAllFavorites chegar. Best-effort se algum ID nao existir mais.
  const handleLoadPreset = (preset) => {
    if (preset.destination && preset.destination.thingId) {
      // Aplica cliente -> aplicacao -> thing (cascata)
      setSelecionado({
        cliente: preset.destination.clienteId
          ? {
              value: preset.destination.clienteId,
              label: preset.destination.clienteLabel,
            }
          : null,
        aplicacao: preset.destination.aplicacaoId
          ? {
              value: preset.destination.aplicacaoId,
              label: preset.destination.aplicacaoLabel,
            }
          : null,
        thing: {
          value: preset.destination.thingId,
          label: preset.destination.thingLabel,
        },
      });
    }

    // Aplica periodo
    if (preset.periodPresetKey && preset.periodPresetKey !== 'custom') {
      const presetDef = PERIOD_PRESETS.find(
        (p) => p.key === preset.periodPresetKey
      );
      if (presetDef) applyPeriodPreset(presetDef);
    }

    // Aplica variaveis (best-effort — IDs que nao existirem em
    // optionsAllFavorites apos o load sao descartados pelo Select).
    if (Array.isArray(preset.variableIds)) {
      setVariaveis(preset.variableIds);
      // testeList precisa do shape {label, value} — vamos restaurar com
      // labels temporarios. Quando optionsAllFavorites carregar (effect
      // do thing), o useEffect abaixo refaz com os labels reais.
      setTesteList(
        preset.variableIds.map((id) => ({ value: id, label: '...' }))
      );
    }

    notifySuccess(`Preset "${preset.name}" carregado`);
  };

  // Quando optionsAllFavorites (vars) + allSetpoints carregam apos load
  // de preset, atualizamos os labels do testeList com os nomes reais.
  useEffect(() => {
    if (!testeList.length) return;
    const lookup = new Map();
    optionsAllFavorites.forEach((v) => lookup.set(String(v.id), v.name));
    allSetpoints.forEach((sp) =>
      lookup.set(`${SETPOINT_KEY}${sp.id}`, sp.label)
    );
    let changed = false;
    const updated = testeList.map((item) => {
      const realLabel = lookup.get(String(item.value));
      if (realLabel && realLabel !== item.label) {
        changed = true;
        return { ...item, label: realLabel };
      }
      return item;
    });
    if (changed) setTesteList(updated);
    // testeList nao entra nas deps pra evitar loop (so atualizamos quando
    // optionsAllFavorites/allSetpoints mudam — depois do load de preset).
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [optionsAllFavorites, allSetpoints]);

  const handleDeletePreset = (presetId, presetNameLabel) => {
    deletePreset(presetId);
    setPresets(listPresets());
    notifySuccess(`Preset "${presetNameLabel}" removido`);
  };

  // === Historico ===
  // 'Regerar' aplica as mesmas configuracoes do historico no form,
  // permitindo ao operador refazer um relatorio identico sem digitar nada.
  // Internamente reusa a logica de handleLoadPreset (mesmo formato).
  const handleRegenerateFromHistory = (entry) => {
    handleLoadPreset(entry);
  };

  const handleClearHistory = () => {
    clearHistory();
    setHistory([]);
    notifySuccess('Histórico limpo');
  };

  // Formato amigavel da data: '14:32 · 16/05/2026'.
  const formatHistoryDate = (ms) => {
    const d = new Date(ms);
    const pad = (n) => String(n).padStart(2, '0');
    return `${pad(d.getHours())}:${pad(d.getMinutes())} · ${pad(d.getDate())}/${pad(d.getMonth() + 1)}/${d.getFullYear()}`;
  };

  // Validacao inline: lista campos pendentes pra gerar relatorio.
  // Usado pra mostrar feedback discreto perto do botao (em vez do botao
  // ficar simplesmente disabled sem explicacao). Memoizado pra evitar
  // recomputo a cada render do parent.
  const pendingFields = useMemo(() => {
    const pending = [];
    if (!selecionado.cliente) pending.push('Cliente');
    if (!selecionado.aplicacao) pending.push('Aplicação');
    if (asThingsArray(selecionado.thing).length === 0) pending.push('Máquina');
    if (!dataInicio) pending.push('Data início');
    if (!dataFinal) pending.push('Data final');
    if (!allVariaveis.length) pending.push('Variáveis');
    return pending;
  }, [selecionado, dataInicio, dataFinal, allVariaveis]);

  const handleSelectChange = (value, newValue) => {
    setVariaveis([]);
    setTesteList([]);
    if (value === 'cliente') {
      setSelecionado({ thing: null, aplicacao: null, [value]: newValue });
    } else if (value === 'aplicacao') {
      setSelecionado({ ...selecionado, thing: null, [value]: newValue });
    } else {
      // Thing pode vir como array (multi-select) ou null (clearAll).
      setSelecionado({ ...selecionado, [value]: newValue });
    }
  };

  return (
    <s.Container className="fade-in">
      <s.DatePickerDarkStyles />
      <s.BreadcrumbWrapper>
        <Breadcumb paths={breadcrumbPaths} />
      </s.BreadcrumbWrapper>

      <s.PageHeader>
        <s.PageTitle>Relatórios</s.PageTitle>
        <s.PageSubtitle>
          Gere relatórios em XLS com dados históricos de variáveis de uma
          máquina. Siga os passos abaixo — você pode salvar configurações
          recorrentes como templates ou regerar de uma só vez via histórico.
        </s.PageSubtitle>
      </s.PageHeader>

      <s.MainGrid>
        <s.FormColumn>

      <s.Section>
        <s.SectionHead>
          <s.SectionTag>
            <s.StepNumber>1</s.StepNumber>
            Destino
          </s.SectionTag>
          <s.SectionHint>
            Escolha a máquina (ou várias) que será o alvo do relatório.
          </s.SectionHint>
        </s.SectionHead>
        <s.SectionContent>
          <s.FieldsRow>
            {/* Cliente e Aplicacao: react-select com menuPortalTarget pra
                escapar do overflow:hidden da Section. menuPosition:fixed
                garante que o menu nao seja afetado pelo scroll do form. */}
            {[
              { label: 'Cliente', value: 'cliente' },
              { label: 'Aplicação', value: 'aplicacao' },
            ].map(({ label, value }) => (
              <s.Field key={value}>
                <s.FieldLabel required>{label}</s.FieldLabel>
                <Select
                  isSearchable
                  isDisabled={!lista[value].length || loading}
                  placeholder=""
                  styles={selectStyles}
                  options={lista[value]}
                  value={selecionado[value]}
                  onChange={(newValue) => handleSelectChange(value, newValue)}
                  menuPortalTarget={PORTAL_TARGET}
                  menuPosition="fixed"
                />
              </s.Field>
            ))}
            {/* Maquina vira multi-select: permite gerar relatorio para
                varias maquinas da mesma aplicacao em sequencia (um XLS
                por maquina baixado automaticamente). Hint inline esclarece. */}
            <s.Field>
              <s.FieldLabel required>Máquina(s)</s.FieldLabel>
              <Select
                isMulti
                isSearchable
                isDisabled={!lista.thing.length || loading}
                placeholder=""
                styles={selectStyles}
                value={asThingsArray(selecionado.thing)}
                options={lista.thing}
                onChange={(newValue) => handleSelectChange('thing', newValue)}
                menuPortalTarget={PORTAL_TARGET}
                menuPosition="fixed"
              />
              {asThingsArray(selecionado.thing).length > 1 && (
                <s.HintLabel>
                  {asThingsArray(selecionado.thing).length} máquinas — será
                  gerado um XLS por máquina.
                </s.HintLabel>
              )}
            </s.Field>
          </s.FieldsRow>
        </s.SectionContent>
      </s.Section>

      <s.Section>
        <s.SectionHead>
          <s.SectionTag>
            <s.StepNumber>2</s.StepNumber>
            Período
          </s.SectionTag>
          <s.SectionHint>
            Defina o intervalo de tempo. Use um atalho ou ajuste as datas
            manualmente — máximo é a data de hoje.
          </s.SectionHint>
        </s.SectionHead>
        <s.SectionContent>
          <s.PresetChipsRow>
            <s.PresetChipsLabel>Atalhos:</s.PresetChipsLabel>
            {PERIOD_PRESETS.map((preset) => (
              <s.PresetChip
                key={preset.key}
                type="button"
                aria-pressed={
                  activePresetKey === preset.key ? 'true' : 'false'
                }
                onClick={() => applyPeriodPreset(preset)}
                disabled={loading}
              >
                {preset.label}
              </s.PresetChip>
            ))}
          </s.PresetChipsRow>
          <s.FieldsRow>
            <s.Field>
              <s.FieldLabel required>Data início</s.FieldLabel>
              <DatePicker
                maxDate={new Date()}
                dateFormat="dd/MM/yyyy"
                locale="pt"
                selected={dataInicio}
                disabled={loading}
                onChange={(date) => handleDateChange(date, 'dataInicio')}
                customInput={
                  <s.ButtonDate>
                    {dataInicio ? newFormatDateButton(dataInicio) : '—'}
                  </s.ButtonDate>
                }
              />
            </s.Field>
            <s.Field>
              <s.FieldLabel required>Data final</s.FieldLabel>
              <DatePicker
                maxDate={new Date()}
                dateFormat="dd/MM/yyyy"
                locale="pt"
                selected={dataFinal}
                disabled={loading}
                onChange={(date) => handleDateChange(date, 'dataFinal')}
                customInput={
                  <s.ButtonDate>
                    {dataFinal ? newFormatDateButton(dataFinal) : '—'}
                  </s.ButtonDate>
                }
              />
            </s.Field>
          </s.FieldsRow>
        </s.SectionContent>
      </s.Section>

      <s.Section>
        <s.SectionHead>
          <s.SectionTag>
            <s.StepNumber>3</s.StepNumber>
            Variáveis
          </s.SectionTag>
          <s.SectionHint>
            Selecione as medições que serão extraídas no XLS. Variáveis e
            setpoints aparecem em grupos separados — você pode escolher
            quantas quiser.
          </s.SectionHint>
        </s.SectionHead>
        <s.SectionContent>
          <s.Field>
            <s.FieldLabel required>Variáveis e setpoints</s.FieldLabel>
            <Select
              isMulti
              isSearchable
              isDisabled={asThingsArray(selecionado.thing).length === 0 || loading}
              placeholder=""
              styles={selectStylesVariables}
              menuPortalTarget={PORTAL_TARGET}
              menuPosition="fixed"
              value={testeList}
              // Grupos no react-select: variaveis e setpoints sao
              // semanticas diferentes (medicao vs referencia), separa-las
              // visualmente ajuda o operador a encontrar o que precisa
              // sem misturar. Options memoizadas em variableSelectOptions.
              options={variableSelectOptions}
              onChange={(valueChanged) => {
                setTesteList(valueChanged);
                const idVariables = [];
                valueChanged.forEach((variableList) => {
                  idVariables.push(variableList.value);
                });
                setVariaveis(idVariables);
              }}
            />
            <s.HintLabel>
              Selecione na lista ou pesquise as variáveis desejadas.
            </s.HintLabel>
          </s.Field>
        </s.SectionContent>
      </s.Section>

      {/* Footer com botoes de geracao DENTRO da FormColumn — flui apos
          as 3 steps do form. Atalhos (templates+historico) ficam na
          sidebar a direita. */}
      <s.Footer>
        {loading ? (
          <s.LoaderRow>
            <Loader />
            <span>
              {cancelRequested
                ? 'Cancelando após o relatório atual…'
                : genProgress && genProgress.total > 1
                ? `Gerando relatório ${genProgress.current} de ${genProgress.total}…`
                : 'Gerando relatório…'}
            </span>
            {genProgress && genProgress.total > 1 && !cancelRequested && (
              <s.IconButton
                type="button"
                danger
                onClick={() => {
                  cancelRef.current = true;
                  setCancelRequested(true);
                }}
              >
                Cancelar
              </s.IconButton>
            )}
          </s.LoaderRow>
        ) : (
          <>
            <s.LimparButton
              type="button"
              onClick={() => {
                setSelecionado({
                  cliente: null,
                  thing: null,
                  aplicacao: null,
                });
                setTesteList([]);
                setVariaveis([]);
                setLista({ ...lista, thing: [], aplicacao: [] });
                applyPeriodPreset(PERIOD_PRESETS[0]);
              }}
            >
              Limpar campos
            </s.LimparButton>
            {pendingFields.length > 0 && (
              <s.PendingPill
                title={`Faltam: ${pendingFields.join(', ')}`}
              >
                {pendingFields.length} campo
                {pendingFields.length > 1 ? 's' : ''} pendente
                {pendingFields.length > 1 ? 's' : ''}
              </s.PendingPill>
            )}
            <s.Button
              type="button"
              disabled={pendingFields.length > 0}
              onClick={handleGenerateNewReport}
            >
              Gerar relatório
              {asThingsArray(selecionado.thing).length > 1 &&
                ` (${asThingsArray(selecionado.thing).length})`}
            </s.Button>
          </>
        )}
      </s.Footer>

        </s.FormColumn>

        {/* Coluna lateral: atalhos (templates + historico). Em viewport
            estreito (<1100px), MainGrid colapsa pra 1 coluna e essa
            sidebar aparece embaixo do form. */}
        <s.SidebarColumn>

      <s.Section>
        <s.SectionTag>Templates</s.SectionTag>
        <s.SectionContent>
          <s.SectionHint>
            Salve configurações recorrentes (destino + período + variáveis)
            pra carregar com 1 clique depois.
          </s.SectionHint>
          <s.SavePresetRow>
            <input
              type="text"
              placeholder="Nome do template (ex.: Mensal SABESP Campo Belo)"
              value={presetName}
              onChange={(e) => setPresetName(e.target.value)}
              maxLength={60}
              disabled={loading}
              onKeyDown={(e) => {
                if (e.key === 'Enter') handleSavePreset();
              }}
            />
            <s.IconButton
              type="button"
              onClick={handleSavePreset}
              disabled={loading || !presetName.trim() || !selecionado.thing}
            >
              Salvar atual
            </s.IconButton>
          </s.SavePresetRow>

          {presets.length === 0 ? (
            <s.EmptyHint>
              Nenhum template salvo. Configure um relatório e clique em
              &quot;Salvar atual&quot; para reusar depois.
            </s.EmptyHint>
          ) : (
            <s.ItemList>
              {presets.map((preset) => (
                <s.Item key={preset.id}>
                  <s.ItemMain>
                    <s.ItemTitle>{preset.name}</s.ItemTitle>
                    <s.ItemMeta>
                      {preset.destination?.thingLabel || '—'}
                      {' · '}
                      {(preset.variableIds || []).length} variáveis
                      {' · '}
                      {PERIOD_PRESETS.find(
                        (p) => p.key === preset.periodPresetKey
                      )?.label || 'período personalizado'}
                    </s.ItemMeta>
                  </s.ItemMain>
                  <s.ItemActions>
                    <s.IconButton
                      type="button"
                      onClick={() => handleLoadPreset(preset)}
                      disabled={loading}
                    >
                      Carregar
                    </s.IconButton>
                    <s.IconButton
                      type="button"
                      danger
                      onClick={() =>
                        handleDeletePreset(preset.id, preset.name)
                      }
                      disabled={loading}
                      title="Remover preset"
                    >
                      ×
                    </s.IconButton>
                  </s.ItemActions>
                </s.Item>
              ))}
            </s.ItemList>
          )}
        </s.SectionContent>
      </s.Section>

      <s.Section>
        <s.SectionTag>Histórico</s.SectionTag>
        <s.SectionContent>
          <s.SectionHint>
            Últimos 10 relatórios gerados. Clique em &quot;Regerar&quot;
            pra refazer um idêntico ao original.
          </s.SectionHint>
          {history.length === 0 ? (
            <s.EmptyHint>
              Nenhum relatório gerado ainda. Os últimos 10 aparecem aqui
              após cada geração.
            </s.EmptyHint>
          ) : (
            <>
              <s.ItemList>
                {history.map((entry) => (
                  <s.Item key={entry.id}>
                    <s.ItemMain>
                      <s.ItemTitle>{entry.thingLabel || '—'}</s.ItemTitle>
                      <s.ItemMeta>
                        {entry.periodLabel}
                        {' · '}
                        {entry.variableCount} variáveis
                        {' · '}
                        gerado em {formatHistoryDate(entry.generatedAt)}
                      </s.ItemMeta>
                    </s.ItemMain>
                    <s.ItemActions>
                      <s.IconButton
                        type="button"
                        onClick={() => handleRegenerateFromHistory(entry)}
                        disabled={loading}
                      >
                        Regerar
                      </s.IconButton>
                    </s.ItemActions>
                  </s.Item>
                ))}
              </s.ItemList>
              <s.Footer>
                <s.IconButton
                  type="button"
                  danger
                  onClick={handleClearHistory}
                  disabled={loading}
                >
                  Limpar histórico
                </s.IconButton>
              </s.Footer>
            </>
          )}
        </s.SectionContent>
      </s.Section>

        </s.SidebarColumn>
      </s.MainGrid>
    </s.Container>
  );
};

export default Relatorios;
